import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import type { Event } from 'react-big-calendar';

import { useCalendar } from 'app/hooks/data/core/extranet/inventory/calendar/useCalendar';
import { useCreateSeries } from 'app/hooks/data/core/extranet/inventory/calendar/useCreateSeries';
import { useCreateSlot } from 'app/hooks/data/core/extranet/inventory/calendar/useCreateSlot';
import { useDeleteSeries } from 'app/hooks/data/core/extranet/inventory/calendar/useDeleteSeries';
import { useDeleteSlot } from 'app/hooks/data/core/extranet/inventory/calendar/useDeleteSlot';
import { useUpdateSeries } from 'app/hooks/data/core/extranet/inventory/calendar/useUpdateSeries';
import { useUpdateSlot } from 'app/hooks/data/core/extranet/inventory/calendar/useUpdateSlot';
import { useValidateCreateSeries } from 'app/hooks/data/core/extranet/inventory/calendar/useValidateCreateSeries';
import { useValidateUpdateSeries } from 'app/hooks/data/core/extranet/inventory/calendar/useValidateUpdateSeries';
import { useValidateUpdateSlot } from 'app/hooks/data/core/extranet/inventory/calendar/useValidateUpdateSlot';
import type { CalendarPeriod } from 'app/typings/core/extranet/inventory/calendar';
import { CALENDAR_SERIES_SCOPE } from 'app/typings/core/extranet/inventory/enums/CalendarSeriesScope';
import type { CalendarSeriesScope } from 'app/typings/core/extranet/inventory/enums/CalendarSeriesScope';

import type { AvailabilityFormValues } from '../_components/AvailabilityForm/availabilityForm.types';
import { getEventKey } from '../utils/events';
import type { IsDayClosed } from '../utils/events';
import { mapCalendarToEvents } from '../utils/mapCalendar';
import {
  isRecurring,
  toApiDate,
  toCreateSeriesPayload,
  toCreateSlotPayload,
  toUpdateSeriesPayload,
  toUpdateSlotPayload,
  toValidateUpdateSlotPayload,
} from '../utils/payload';
import {
  EDIT_SCOPES,
  REPEAT_SCOPES,
  getResource,
  isRecurrentEvent,
  isRepeatChanged,
} from '../utils/series';

type PendingEdit =
  | {
      kind: 'edit';
      event: Event;
      values: AvailabilityFormValues;
      scopes: CalendarSeriesScope[];
    }
  | { kind: 'delete'; event: Event; scopes: CalendarSeriesScope[] };

type PendingOverlap =
  | { kind: 'create'; values: AvailabilityFormValues }
  | {
      kind: 'edit';
      event: Event;
      values: AvailabilityFormValues;
      scope?: CalendarSeriesScope;
    };

type Params = {
  clearPreview: () => void;
  isDayClosed: IsDayClosed;
  hotelId?: number;
  calendarId?: number;
  period: CalendarPeriod;
};

const numericId = (value?: string): number | undefined => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

export const useTimeslotEvents = ({
  clearPreview,
  isDayClosed,
  hotelId,
  calendarId,
  period,
}: Params) => {
  const [pendingOverlap, setPendingOverlap] = useState<PendingOverlap | null>(
    null
  );
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const { data: calendar, isFetching } = useCalendar(
    hotelId,
    calendarId,
    period
  );

  const events = useMemo(
    () => mapCalendarToEvents(calendar, period, isDayClosed),
    [calendar, period, isDayClosed]
  );

  const { mutate: createSlot } = useCreateSlot(hotelId, calendarId, period);
  const { mutate: createSeries } = useCreateSeries(hotelId, calendarId, period);
  const { mutate: updateSlot } = useUpdateSlot(hotelId, calendarId, period);
  const { mutate: updateSeries } = useUpdateSeries(hotelId, calendarId, period);
  const { mutate: deleteSlot } = useDeleteSlot(hotelId, calendarId, period);
  const { mutate: deleteSeries } = useDeleteSeries(hotelId, calendarId, period);

  const { mutateAsync: validateCreateSeries, isLoading: isValidatingCreate } =
    useValidateCreateSeries(hotelId, calendarId);
  const { mutateAsync: validateUpdateSeries, isLoading: isValidatingUpdate } =
    useValidateUpdateSeries(hotelId, calendarId);
  const {
    mutateAsync: validateUpdateSlot,
    isLoading: isValidatingSlotConversion,
  } = useValidateUpdateSlot(hotelId, calendarId);

  const persistCreate = useCallback(
    (values: AvailabilityFormValues) => {
      clearPreview();

      if (!calendarId) {
        return;
      }

      if (isRecurring(values)) {
        createSeries(toCreateSeriesPayload(values));
      } else {
        createSlot(toCreateSlotPayload(values));
      }
    },
    [calendarId, createSeries, createSlot, clearPreview]
  );

  const persistEdit = useCallback(
    (
      event: Event,
      values: AvailabilityFormValues,
      scope?: CalendarSeriesScope
    ) => {
      clearPreview();

      if (!calendarId) {
        return;
      }

      const seriesId = numericId(getResource(event)?.seriesId);
      const slotId = numericId(getEventKey(event));

      if (isRecurring(values)) {
        if (seriesId !== undefined) {
          updateSeries({
            seriesId,
            payload: toUpdateSeriesPayload(
              values,
              scope ?? CALENDAR_SERIES_SCOPE.THIS_AND_NEXT
            ),
          });
        } else if (slotId !== undefined) {
          deleteSlot(slotId);
          createSeries(toCreateSeriesPayload(values));
        }
      } else if (slotId !== undefined) {
        updateSlot({ slotId, payload: toUpdateSlotPayload(values) });
      }
    },
    [
      calendarId,
      updateSeries,
      updateSlot,
      deleteSlot,
      createSeries,
      clearPreview,
    ]
  );

  const persistDelete = useCallback(
    (event: Event, scope?: CalendarSeriesScope) => {
      if (!calendarId) {
        return;
      }

      const seriesId = numericId(getResource(event)?.seriesId);
      const slotId = numericId(getEventKey(event));

      if (seriesId !== undefined) {
        deleteSeries({
          seriesId,
          scope: scope ?? CALENDAR_SERIES_SCOPE.ALL,
          date: toApiDate(dayjs(event.start)),
        });
      } else if (slotId !== undefined) {
        deleteSlot(slotId);
      }
    },
    [calendarId, deleteSeries, deleteSlot]
  );

  const hasCreateOverlap = useCallback(
    async (values: AvailabilityFormValues) => {
      try {
        const { hasOverlap } = await validateCreateSeries(
          toCreateSeriesPayload(values)
        );

        return hasOverlap;
      } catch {
        return false;
      }
    },
    [validateCreateSeries]
  );

  const hasUpdateOverlap = useCallback(
    async (
      seriesId: number,
      values: AvailabilityFormValues,
      scope: CalendarSeriesScope
    ) => {
      try {
        const { hasOverlap } = await validateUpdateSeries({
          seriesId,
          payload: toUpdateSeriesPayload(values, scope),
        });

        return hasOverlap;
      } catch {
        return false;
      }
    },
    [validateUpdateSeries]
  );

  const hasSlotConversionOverlap = useCallback(
    async (slotId: number, values: AvailabilityFormValues) => {
      try {
        const { hasOverlap } = await validateUpdateSlot({
          slotId,
          payload: toValidateUpdateSlotPayload(values),
        });

        return hasOverlap;
      } catch {
        return false;
      }
    },
    [validateUpdateSlot]
  );

  const handleCreateEvent = useCallback(
    async (values: AvailabilityFormValues) => {
      if (isRecurring(values) && (await hasCreateOverlap(values))) {
        setPendingOverlap({ kind: 'create', values });

        return;
      }

      persistCreate(values);
    },
    [hasCreateOverlap, persistCreate]
  );

  const handleConfirmOverlap = useCallback(() => {
    if (!pendingOverlap) {
      return;
    }

    if (pendingOverlap.kind === 'create') {
      persistCreate(pendingOverlap.values);
    } else {
      persistEdit(
        pendingOverlap.event,
        pendingOverlap.values,
        pendingOverlap.scope
      );
    }

    setPendingOverlap(null);
  }, [pendingOverlap, persistCreate, persistEdit]);

  const handleSaveEvent = useCallback(
    async (event: Event, values: AvailabilityFormValues) => {
      if (!isRecurrentEvent(event)) {
        const slotId = numericId(getEventKey(event));

        if (
          isRecurring(values) &&
          slotId !== undefined &&
          (await hasSlotConversionOverlap(slotId, values))
        ) {
          setPendingOverlap({ kind: 'edit', event, values });

          return;
        }

        persistEdit(event, values);

        return;
      }

      const scopes = isRepeatChanged(event, values)
        ? REPEAT_SCOPES
        : EDIT_SCOPES;

      setPendingEdit({ kind: 'edit', event, values, scopes });
    },
    [persistEdit, hasSlotConversionOverlap]
  );

  const handleDeleteEvent = useCallback(
    (event: Event) => {
      if (!isRecurrentEvent(event)) {
        persistDelete(event);

        return;
      }

      setPendingEdit({ kind: 'delete', event, scopes: EDIT_SCOPES });
    },
    [persistDelete]
  );

  const handleConfirmEdit = useCallback(
    async (scope: CalendarSeriesScope) => {
      if (!pendingEdit) {
        return;
      }

      if (pendingEdit.kind === 'delete') {
        persistDelete(pendingEdit.event, scope);
        setPendingEdit(null);

        return;
      }

      const seriesId = numericId(getResource(pendingEdit.event)?.seriesId);

      if (
        seriesId !== undefined &&
        isRecurring(pendingEdit.values) &&
        (await hasUpdateOverlap(seriesId, pendingEdit.values, scope))
      ) {
        setPendingOverlap({
          kind: 'edit',
          event: pendingEdit.event,
          values: pendingEdit.values,
          scope,
        });
        setPendingEdit(null);

        return;
      }

      persistEdit(pendingEdit.event, pendingEdit.values, scope);
      setPendingEdit(null);
    },
    [pendingEdit, persistEdit, persistDelete, hasUpdateOverlap]
  );

  const cancelOverlap = useCallback(() => setPendingOverlap(null), []);
  const cancelEdit = useCallback(() => setPendingEdit(null), []);

  return {
    events,
    isLoading: isFetching,
    isValidatingOverlap:
      isValidatingCreate || isValidatingUpdate || isValidatingSlotConversion,
    pendingOverlap,
    pendingEdit,
    handleCreateEvent,
    handleConfirmOverlap,
    handleSaveEvent,
    handleDeleteEvent,
    handleConfirmEdit,
    cancelOverlap,
    cancelEdit,
  };
};
